import { useEffect, type ReactNode } from 'react';
import { Button } from '@monksflow/monks-ui';
import { ArrowLeftIcon, XIcon } from 'lucide-react';

const PANEL_WIDTH = 420;

interface SlideOverPanelProps {
  open: boolean;
  onClose: () => void;
  /** Names the region for assistive tech, and labels the close control. */
  label: string;
  closeLabel: string;
  title: ReactNode;
  /** Renders a back control ahead of the title when the panel has a second level. */
  onBack?: () => void;
  backLabel?: string;
  testId?: string;
  children: ReactNode;
}

/**
 * The right-hand drawer, sitting in the flow beside the report rather than floating over it:
 * a reader checks the panel against the prose that prompted it, so the report has to stay
 * visible and undimmed. Place it as a sibling of the report, in the page's panel row.
 *
 * Opening and closing animates the width, matching `AnalysisSidebar` at the other end of the
 * same row. That means staying mounted at zero width rather than unmounting: an element that
 * has left the tree cannot transition out. Callers therefore keep rendering it while closed,
 * and only stop passing children once there is nothing left to draw.
 *
 * Presentational: the caller owns what is shown and when, so a panel can be rendered on its
 * own in a test or a story.
 */
export const SlideOverPanel = ({
  open,
  onClose,
  label,
  closeLabel,
  title,
  onBack,
  backLabel,
  testId,
  children,
}: SlideOverPanelProps) => {
  /* On the document, not the panel: a reader who has just clicked the control that opened
     this still has focus on it, outside this subtree, and that is where Escape is most
     likely pressed. */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <aside
      aria-label={label}
      data-testid={testId}
      inert={!open}
      /* Resizing while staying on screen, so ease-out in both directions. The negative margin
         cancels the row's `gap-2`, which a zero-width flex item is still given and would
         otherwise leave as a dead strip beside the report. */
      className="h-full shrink-0 overflow-hidden transition-[width,margin] duration-base ease-out"
      style={{ width: open ? PANEL_WIDTH : 0, marginLeft: open ? 0 : -8 }}
    >
      <div
        className="flex h-full flex-col overflow-hidden rounded-lg bg-card dark:bg-background"
        style={{ width: PANEL_WIDTH }}
      >
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-border-light py-0 pl-5 pr-3">
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              className="size-8 shrink-0 p-0"
              aria-label={backLabel}
              onClick={onBack}
            >
              <ArrowLeftIcon className="size-4 text-foreground-alt" />
            </Button>
          )}
          <span className="min-w-0 flex-1 truncate text-base font-medium">{title}</span>
          <Button
            variant="ghost"
            size="sm"
            className="size-8 shrink-0 p-0"
            aria-label={closeLabel}
            onClick={onClose}
          >
            <XIcon className="size-4 text-foreground-alt" />
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-auto p-5">{children}</div>
      </div>
    </aside>
  );
};

SlideOverPanel.displayName = 'SlideOverPanel';
